import styles from "scss/Page.module.scss";
import AddButton from "components/Shared/Button/AddButton";
import { toast } from "react-toastify";
import { Modal } from "components/Modal";
import { UserRoles } from "Enums/UserRoles";
import { useDispatch } from "react-redux";
import { DynamicFilter } from "components/Shared/DynamicFilter";
import { useAppSelector } from "redux/store";
import { Box, Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { UserPaginationFiltersForQuery } from "Interfaces/UserFilters";
import { Field, FullScreenLoader, PageTitle, User } from "components";
import { useGetUsersQuery, useUploadUserMutation} from "redux/api/userApi";
import { EDynamicColumnType, IDynamicColumnFilter, ISelectOption } from "Interfaces/Utils";
import { IUserState, removeCurrentFilters, updateFilters } from "redux/features/userSlice";
import { useGetAllCompaniesQuery } from "redux/api/companyApi";
import { ICompany } from "Models/Company";
import { useGetPointOfSaleByCompanyQuery } from "redux/api/pointOfSaleApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { IUserLoggedState } from "redux/features/loggedUserSlice";
import { useIntl } from "react-intl";


/**
 * Component to render users main page
 */

export const UserPage = () => {
  const { formatMessage } = useIntl();

  const PAGE_TITLE = formatMessage({ id: "user.title" });
  const dispatch = useDispatch();

  // App state selectors


  const { filters, pageIndex, pageSize, sort } = useAppSelector((state: { userState: IUserState }) => state.userState);
  
  //local state
  const [pointsOfSale, setPointsOfSale] = useState<ISelectOption[]>([]);
  
  // API calls
  const { data: companies, isLoading: isCompaniesLoading } = useGetAllCompaniesQuery();
  
  const [uploadFilesForUsers] = useUploadUserMutation();
  const { data, isLoading, refetch } = useGetUsersQuery({
    filters: { ...filters },
    pageIndex: pageIndex,
    pageSize: pageSize,
    sort: sort,
  });
  
  const [companyId,setCompanyId] = useState<string>("");
  const { data: pointsOfSaleData } = useGetPointOfSaleByCompanyQuery(companyId || skipToken,{skip: !companyId});

  // Component state

  const [files, setFiles] = useState<File[]>();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);



  
  // Refetch management

  useEffect(() => {
    refetch();
  }, [data?.data, refetch]);

  // Actualizar los puntos de venta dinámicamente al cambiar `selectedCompany`
  useEffect(() => {
    
    if (pointsOfSaleData) {
      const transformedPointsOfSale = pointsOfSaleData.map((pos) => ({
        value: pos.id,
        label: pos.name,
      }));
      setPointsOfSale(transformedPointsOfSale);
    }
  }, [pointsOfSaleData]);

  // Select options
  const groupSelectOptions: ISelectOption[] = useMemo(() => {

    if (!companies||!companies) return [];

    return companies.map((company: ICompany) => ({
      label: company.name,
      value: company._id,
    }));
    
  }, [companies])
  const ROLE_OPTIONS = [
    {
      label: formatMessage({ id: "userRoles.ADMIN" }),
      value: UserRoles.ADMIN,
    },
    {
      label: formatMessage({ id: "userRoles.SUPERADMIN" }),
      value: UserRoles.SUPERADMIN,
    },
    {
      label: formatMessage({ id: "userRoles.MANAGER" }),
      value: UserRoles.MANAGER,
    },
    {
      label: formatMessage({ id: "userRoles.TECHNICIAN" }),
      value: UserRoles.TECHNICIAN,
    }
    
  ];
  const { user } = useAppSelector(
    (state: { userLoggedState: IUserLoggedState }) => state.userLoggedState
  );
  const filteredRoleOptions = user?.role === UserRoles.SUPERADMIN
  ? ROLE_OPTIONS
  : user?.role === UserRoles.ADMIN 
    ? ROLE_OPTIONS.filter(option => option.value !== UserRoles.SUPERADMIN)
    : ROLE_OPTIONS.filter(option => option.value !== UserRoles.SUPERADMIN && option.value !== UserRoles.ADMIN);
 
  
  // Modal management

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const onAcceptDeleteModal = () => {
    if (files && files?.length > 0) {
      const formData = new FormData();

      formData.append("file", files[0] as Blob);

      uploadFilesForUsers(formData);
      setOpenDeleteModal(false);
    } else {
      toast.error(`Debes subir un archivo`);
    }
  };

  // Table columns definition

  const columnFilters: IDynamicColumnFilter[] = useMemo(() => {
    const filters: IDynamicColumnFilter[] = [
      {
        type: EDynamicColumnType.STRING,
        grid: 6,
        field: "searchText",
        label: formatMessage({ id: "user.name" }),
      },
      {
        type: EDynamicColumnType.STRING,
        grid: 6,
        field: "email",
        label: formatMessage({ id: "user.email" }),
      },
      {
        type: EDynamicColumnType.SELECT,
        grid: 6,
        field: "pointOfSaleId",
        label: formatMessage({ id: "pointOfSale.title" }),
        values: pointsOfSale,
      },
      {
        type: EDynamicColumnType.SELECT,
        grid: 6,
        field: "role",
        label: formatMessage({ id: "user.role" }),
        values: filteredRoleOptions,
      },
    ];
  
    // Agregar el filtro de empresa solo si el rol es SUPERADMIN
    if (user?.role === UserRoles.SUPERADMIN) {
      filters.splice(2, 0, {
        type: EDynamicColumnType.SELECT,
        grid: 6,
        field: "companyId",
        label: formatMessage({ id: "company.title" }),
        values: groupSelectOptions,
      });
    }
  
    return filters;
  }, [pointsOfSale, filteredRoleOptions, groupSelectOptions, user?.role]);

  useEffect(() => {
    refetch();
  }, [filters, refetch]);
  

  // Table management

  const handleSubmit = (filters: Record<string, any>) => {
      const updatedFilters = {
        ...filters,
        //...provisionalFilters,
      };

      dispatch(updateFilters(updatedFilters as UserPaginationFiltersForQuery));
  };

  const removeFilter = (code: string) => {
    dispatch(removeCurrentFilters(code));
  };

  if (isLoading||isCompaniesLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Grid container className={styles.viewContainer}>
      <Grid item xs={12} >
        <Box display={"flex"} justifyContent={"space-between"}>
          <Box display={"flex"} alignItems={"center"} gap="20px" flexWrap={"wrap"}>
            <PageTitle pageTitle={PAGE_TITLE} />
            <DynamicFilter.AppliedFilters filters={columnFilters} filterValues={filters} handleRemoveFilter={removeFilter} />
          </Box>
          <Grid item xs={12} md={4}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  width: "480px !important",
                  position: "relative",
                }}
              >
                <DynamicFilter.Toolbar
                  filters={columnFilters}
                  externalFilterValues={filters}
                  filterData={handleSubmit}
                  onOtherComponentChange={(value: string)=>{
                    if(filters.companyId?.value !== value){                      
                      setCompanyId(value);
                      handleSubmit({...filters,companyId:{value}});                   
                    }                  
                  }}
                  resetGlobalFilters={() => {
                    dispatch(updateFilters({})); // Limpia todos los filtros globales
                    dispatch(removeCurrentFilters("companyId"));               
                  }}
                />
                <AddButton urlName={"/crear-usuario"}  label={formatMessage({ id: "company.add" })}/>
              </div>
            </div>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={16}>
        <Card className={styles.pageCard} sx={{ padding: "0" }}>
          <Modal.AddModal
            open={openDeleteModal}
            title={
              "Añade el archivo que contiene a los usuarios que se desean subir"
            }
            onAcceptHandler={onAcceptDeleteModal}
            onCancelHandler={onCloseDeleteModal}
          >
            <Field.FileDropzone files={files} setFiles={setFiles} />
          </Modal.AddModal>
          <User.Table paginatedQuery={data} isLoading={isLoading} />
        </Card>
      </Grid>
    </Grid>
  );
};
